/* ============================================================
   <goodnight-product> custom element.
   Owns:
     - variant switching driven by [data-variant-radio]
     - gallery modal (open by click, close by backdrop/X/Escape)
     - sticky bottom ATC visibility (IntersectionObserver on the
       [data-above-fold-end] sentinel)
   Variants payload is read from the inline JSON in
   [data-variants-json]. Money values inside that payload are
   in MAJOR currency units (numeric, e.g. 2199.00) because we
   render them with the `money_without_currency` filter at
   build time — see goodnight-product-main.liquid.
   ============================================================ */

(() => {
  if (customElements.get('goodnight-product')) return;

  class GoodnightProduct extends HTMLElement {
    connectedCallback() {
      try {
        const dataNode = this.querySelector('[data-variants-json]');
        this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];
      } catch (err) {
        console.warn('[goodnight-product] variants JSON parse failed', err);
        this.variants = [];
      }

      this.priceEl       = this.querySelector('[data-price]');
      this.compareEl     = this.querySelector('[data-compare-at]');
      this.atcEl         = this.querySelector('[data-atc-button]');
      this.stickyAtcEl   = this.querySelector('[data-sticky-atc]');
      this.stickyMetaEl  = this.querySelector('[data-sticky-meta]');
      this.idInput       = this.querySelector('input[name="id"]');
      this.installEl     = this.querySelector('[data-installments]');
      this.currencySym   = this.dataset.currencySymbol || '$';

      this.querySelectorAll('[data-variant-radio]').forEach((input) => {
        input.addEventListener('change', () => this.onVariantChange());
      });

      this.bindGallery();
      this.bindStickyVisibility();
      this.bindCarousel();
      this.onVariantChange();
    }

    /* ---- Variant switching ---- */

    onVariantChange() {
      const radios = Array.from(this.querySelectorAll('[data-variant-radio]:checked'));
      if (radios.length === 0) return;

      // Build the selected-options array, e.g. ['Queen', 'Medium'].
      const selectedOptions = radios
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((r) => r.value);

      const variant = this.variants.find((v) =>
        v.options.length === selectedOptions.length &&
        v.options.every((opt, i) => opt === selectedOptions[i])
      );
      if (!variant) return;

      if (this.idInput) this.idInput.value = variant.id;

      const priceText = this.formatMoney(variant.price);
      if (this.priceEl) this.priceEl.textContent = priceText;

      if (this.compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.compareEl.textContent = this.formatMoney(variant.compare_at_price);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }

      const atcLabel = variant.available ? `Add to Bed — ${priceText}` : 'Sold out';
      if (this.atcEl) {
        this.atcEl.textContent = atcLabel;
        this.atcEl.disabled = !variant.available;
      }
      if (this.stickyAtcEl) {
        this.stickyAtcEl.textContent = variant.available ? `Add — ${priceText}` : 'Sold out';
        this.stickyAtcEl.disabled = !variant.available;
      }
      if (this.stickyMetaEl) {
        this.stickyMetaEl.textContent = `${selectedOptions.join(' · ')}`;
      }
      if (this.installEl) {
        const per4 = (variant.price / 4);
        this.installEl.innerHTML = `or 4 payments of <strong>${this.formatMoney(per4)}</strong> at checkout.`;
      }

      this.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
    }

    formatMoney(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '';
      // Match Liquid's `money_without_trailing_zeros`: hide ".00" on whole dollars,
      // keep cents when they're non-zero (e.g. 749.75 for installments).
      const hasCents = Math.round(num * 100) % 100 !== 0;
      return `${this.currencySym}${num.toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      })}`;
    }

    /* ---- Gallery modal ---- */

    bindGallery() {
      this.modal     = this.querySelector('[data-gallery-modal]');
      if (!this.modal) return;
      this.modalImg  = this.modal.querySelector('[data-modal-img]');
      this.modalClose= this.modal.querySelector('[data-modal-close]');

      this.querySelectorAll('[data-gallery-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal(trigger);
        });
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.openModal(trigger);
          }
        });
      });

      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target.closest('[data-modal-close]')) {
          this.closeModal();
        }
      });

      this._escListener = (e) => { if (e.key === 'Escape' && !this.modal.hidden) this.closeModal(); };
      document.addEventListener('keydown', this._escListener);

      this._modalTrapListener = (e) => {
        if (e.key !== 'Tab' || this.modal.hidden) return;
        const focusables = this._modalFocusables;
        if (!focusables || focusables.length === 0) { e.preventDefault(); return; }
        const first = focusables[0];
        const last  = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      };
      this.modal.addEventListener('keydown', this._modalTrapListener);
    }

    openModal(trigger) {
      if (!this.modal || !this.modalImg) return;
      this._modalTrigger = trigger;
      const fullSrc = trigger.dataset.fullSrc || trigger.querySelector('img')?.src;
      const alt = trigger.dataset.alt || trigger.querySelector('img')?.alt || '';
      if (fullSrc) {
        this.modalImg.src = fullSrc;
        this.modalImg.alt = alt;
      }
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      this._modalFocusables = Array.from(this.modal.querySelectorAll(focusableSelector))
        .filter((el) => !el.disabled && !el.hidden);
      this.modalClose?.focus();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.style.overflow = '';
      this._modalTrigger?.focus();
      this._modalTrigger = null;
    }

    /* ---- Sticky bar visibility ---- */

    bindStickyVisibility() {
      const sentinel = this.querySelector('[data-above-fold-end]');
      if (!sentinel || typeof IntersectionObserver === 'undefined') return;
      this._observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        // Sentinel sits at the bottom of the info column. When it scrolls
        // out of view above the viewport, show the sticky bar.
        const aboveViewport = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
        this.classList.toggle('is-sticky-visible', aboveViewport);
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
      this._observer.observe(sentinel);
    }

    disconnectedCallback() {
      if (this._escListener) document.removeEventListener('keydown', this._escListener);
      if (this._modalTrapListener && this.modal) this.modal.removeEventListener('keydown', this._modalTrapListener);
      if (this._observer) this._observer.disconnect();
    }

    bindCarousel() {
      const carousel = this.querySelector('[data-carousel]');
      if (!carousel) return;

      const slidesEl = carousel.querySelector('[data-slides]');
      const slides   = Array.from(carousel.querySelectorAll('[data-slide]'));
      const prevBtn  = carousel.querySelector('[data-prev]');
      const nextBtn  = carousel.querySelector('[data-next]');
      const counter  = carousel.querySelector('[data-counter]');
      const dots     = Array.from(carousel.querySelectorAll('[data-dot]'));

      if (slides.length <= 1) return;

      let idx = 0;

      const update = () => {
        slidesEl.style.transform = `translateX(-${idx * 100}%)`;
        slides.forEach((s, i) => s.setAttribute('aria-hidden', i === idx ? 'false' : 'true'));
        dots.forEach((d, i)   => d.setAttribute('aria-selected', i === idx ? 'true' : 'false'));
        if (counter) counter.textContent = `${idx + 1} / ${slides.length}`;
      };

      const goTo = (n) => {
        idx = ((n % slides.length) + slides.length) % slides.length;
        update();
      };

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(idx - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(idx + 1));

      dots.forEach((dot) => {
        dot.addEventListener('click', () => goTo(Number(dot.dataset.dot)));
      });

      carousel.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(idx - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(idx + 1); }
      });

      // ---- Touch / pointer swipe (mobile refinement 2026-05-17) ----
      let startX = null, startY = null, dx = 0, dy = 0;
      const SWIPE_THRESHOLD = 50; // px

      carousel.addEventListener('pointerdown', (e) => {
        if (e.target.closest('[data-prev], [data-next], [data-dot]')) return;
        startX = e.clientX;
        startY = e.clientY;
        dx = 0;
        dy = 0;
      });

      carousel.addEventListener('pointermove', (e) => {
        if (startX === null) return;
        dx = e.clientX - startX;
        dy = e.clientY - startY;
      });

      const endSwipe = () => {
        if (startX === null) return;
        // Only trigger on horizontal-dominant swipes exceeding threshold
        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          if (dx >  0) goTo(idx - 1);
          if (dx <  0) goTo(idx + 1);
        }
        startX = null;
        startY = null;
        dx = 0;
        dy = 0;
      };

      carousel.addEventListener('pointerup', endSwipe);
      carousel.addEventListener('pointercancel', endSwipe);
      carousel.addEventListener('pointerleave', endSwipe);

      this.goToSlide = goTo;

      if (!carousel.hasAttribute('tabindex')) carousel.setAttribute('tabindex', '0');

      update();
    }
  }

  customElements.define('goodnight-product', GoodnightProduct);
})();
